import { useState } from "react";
import { BuzerIcon } from "./BuzerIcons";
import {
  Text, Display, Title, Heading, Eyebrow, Numeral, Micro,
  Button, IconButton, Card, Field, Input, Textarea, Select, Toggle, Pill, Segmented,
  List, ListRow, Lead, Avatar, CheckButton, StatCard, ProgressBar, LevelProgress,
  BadgeGrid, Sheet, Fab, Tag, SectionHeader, Caption, StateDot,
  DifficultyTag, StreakTag, LeadWithStars, DifficultyPicker,
} from "./components/ui/ds";
import { HEAD_FLAIRS, HEAD_FLAIR_CODES, HeadFlair } from "./components/ui/HeadFlair";

// In-app kitchen sink for the v2 primitives. Dev-only preview, mounted from
// main.tsx when the URL hash is #kitchen. Not part of the shipped app; removed
// (or kept behind the hash) after the redesign lands.

const THEMES = [
  ["green", "#6E7E5E"], ["violet", "#7C3AED"], ["cobalt", "#0A4DFF"],
  ["terracotta", "#C2552F"], ["rose", "#C2486B"], ["teal", "#1F8A7D"], ["gold", "#C49A3A"],
];

function Section({ eyebrow, title, children }) {
  return (
    <section style={{ padding: "34px 0 8px", borderTop: "1px solid var(--neutral-border)" }}>
      <Eyebrow style={{ color: "var(--brand-primary)", marginBottom: 8 }}>{eyebrow}</Eyebrow>
      <Heading style={{ marginBottom: 18 }}>{title}</Heading>
      {children}
    </section>
  );
}

export default function DesignKitchen() {
  const [theme, setTheme] = useState("green");
  const [dark, setDark] = useState(false);
  const [toggles, setToggles] = useState({ a: true, b: false, c: true });
  const [pills, setPills] = useState({ Martin: true, Šuro: false, Vlado: true, Viki: false });
  const [listView, setListView] = useState("person");
  const [sheet, setSheet] = useState(false);
  const [diff, setDiff] = useState("normal");   // v29 — ukážka DifficultyPicker
  const [kitchenFlair, setKitchenFlair] = useState(HEAD_FLAIR_CODES[0]);  // v30 — ozdoby na hlavu

  function pickTheme(t) {
    setTheme(t);
    document.documentElement.setAttribute("data-theme", t);
  }
  function toggleDark() {
    const next = !dark; setDark(next);
    document.documentElement.classList.toggle("dark", next);
  }

  const persons = [
    { initial: "M", tone: "#c98a16", title: "Martin", meta: "Klikni a zobraz profil" },
    { initial: "Š", tone: "#2563c9", title: "Šuro", meta: "Klikni a zobraz profil" },
    { initial: "B", tone: "#0f7d72", title: "Barbora Káplocká", meta: "Klikni a zobraz profil" },
  ];
  const activity = [
    { rank: 1, initial: "T", tone: "var(--brand-primary)", title: "Táňa Poláková", meta: "TY · Lv 4", points: "80 b." },
    { rank: 2, initial: "M", tone: "#c98a16", title: "Martin", meta: "Lv 3", points: "70 b." },
    { rank: 3, initial: "V", tone: "#7d3a9e", title: "Vlado", meta: "Lv 2", points: "15 b." },
  ];
  const shop = [
    { icon: "shield", tone: "var(--brand-primary)", title: "Záchranný kruh", meta: "Ochráni sériu pri vynechaní dňa", price: "120 b." },
    { icon: "sparkles", tone: "#0f7d72", title: "Morská zelená", meta: "Odomkni novú tému aplikácie", price: "500 b." },
    { icon: "crown", tone: "#d2a02c", title: "Zlatý rámik", meta: "Rámik okolo profilovej fotky", price: "400 b." },
  ];
  const badges = [
    { name: "Prvý krok", icon: "sparkles", earned: true },
    { name: "Spoločenský", icon: "people", earned: true },
    { name: "Týždeň v kuse", icon: "alarm", earned: false },
    { name: "Disciplína", icon: "shield", earned: false },
    { name: "Stovkár", icon: "target", earned: false },
    { name: "Tisícka", icon: "crown", earned: false },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "var(--neutral-bg)", color: "var(--neutral-text-main)", fontFamily: "var(--ff-ui)" }}>
      {/* sticky header — theme + mode switch */}
      <header style={{ position: "sticky", top: 0, zIndex: 40, background: "color-mix(in srgb, var(--neutral-surface) 82%, transparent)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--neutral-border)", padding: "12px 20px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}> /* mali-ok: dev preview (DesignKitchen), nie v user flow */
        <Eyebrow style={{ color: "var(--brand-primary)" }}>Buzerlístok · DS v2</Eyebrow>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 6 }}>
          {THEMES.map(([t, hex]) => (
            <button key={t} onClick={() => pickTheme(t)} title={t}
              style={{ width: 22, height: 22, borderRadius: 99, background: hex, cursor: "pointer", padding: 0,
                border: theme === t ? "2px solid var(--neutral-surface)" : "2px solid transparent",
                outline: theme === t ? "2px solid var(--neutral-text-main)" : "none" }} />
          ))}
        </div>
        <Segmented options={[{ value: false, label: "Svetlý" }, { value: true, label: "Tmavý" }]}
          value={dark} onChange={toggleDark} />
      </header>

      <main style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px 140px" }}>
        <div style={{ padding: "44px 0 8px" }}>
          <Display>Dnes.</Display>
          <Text variant="bodyLg" tone="muted" style={{ marginTop: 14, maxWidth: "48ch" }}>
            Živá ukážka dizajnového systému — prepni tému alebo režim hore a sleduj, ako sa všetko pretónuje z jednej premennej.
          </Text>
        </div>

        <Section eyebrow="Foundations" title="Typografia">
          <Card style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Display>Display · Fraunces</Display>
            <Title>H1 · Oficiálne výzvy</Title>
            <Heading>H2 · Rebríček týždňa</Heading>
            <Text variant="h3">H3 · Ranná rutina</Text>
            <Text variant="bodyLg">Body Large — zapoj sa do oficiálnych výziev a stúpaj v rebríčku.</Text>
            <Text variant="body">Body — po dobu jedného týždňa si každý deň dáme spolu 1 min plank.</Text>
            <Text variant="label">Label · Body za splnenie</Text>
            <Eyebrow>Eyebrow · Výzvy a súťaže</Eyebrow>
            <Numeral style={{ fontSize: 18 }}>+15 · 225 b. · Lv 4</Numeral>
            <Micro>Micro — pred 23 min · hotové 2026-06-29</Micro>
          </Card>
        </Section>

        <Section eyebrow="03 — Components" title="Tlačidlá">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
            <Button variant="primary">Vytvoriť výzvu</Button>
            <Button variant="secondary">Skús</Button>
            <Button variant="ghost">Zrušiť</Button>
            <Button variant="destructive">Zmazať účet</Button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            <Button variant="primary" size="sm"><BuzerIcon name="add" size={16} color="currentColor" /> Pridať</Button>
            <Button variant="secondary" size="sm">Malé</Button>
            <Button variant="primary" disabled>Disabled</Button>
          </div>
          <Button variant="primary" block style={{ marginTop: 12 }}>Uložiť (block)</Button>

          <Eyebrow style={{ margin: "22px 0 10px" }}>Okrúhle ikonové tlačidlá</Eyebrow>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            <IconButton variant="neutral" icon="mail" aria-label="Správa" />
            <IconButton variant="brand" icon="chevron" aria-label="Ďalej" />
            <IconButton variant="ghost" icon="close" aria-label="Zavrieť" />
            <IconButton variant="destructive" icon="trash" aria-label="Zmazať" />
            <IconButton variant="primary" icon="add" aria-label="Pridať" />
            <IconButton variant="primary" size="lg" icon="add" aria-label="Vytvoriť" />
          </div>
          <Micro style={{ display: "block", marginTop: 8 }}>Neutral · Brand · Ghost · Destructive · Primary — správa/šípka v zozname, zavretie modalu, mazanie a FAB.</Micro>
        </Section>

        <Section eyebrow="03 — Components" title="Formuláre a ovládanie">
          <div style={{ display: "grid", gap: 14, gridTemplateColumns: "1fr" }}>
            <Card>
              <Field label="NÁZOV VÝZVY"><Input defaultValue="Vypiť 1.5 dcl kombuche" /></Field>
              <Field label="POPIS" style={{ marginTop: 14 }}><Textarea defaultValue="Po dobu týždňa každý deň…" /></Field>
              <Field label="OPAKOVANIE" style={{ marginTop: 14 }}>
                <Select defaultValue="d"><option value="d">Každý deň</option><option value="w">Každý týždeň</option></Select>
              </Field>
            </Card>
            <Card>
              <Eyebrow style={{ marginBottom: 4 }}>Prepínače</Eyebrow>
              {[["a", "Všetky upozornenia"], ["b", "Šťuchnutia"], ["c", "Pripomienka nálady"]].map(([k, label]) => (
                <div key={k} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 0", borderTop: "1px solid var(--neutral-border)" }}>
                  <Text variant="h3" style={{ flex: 1, fontSize: 14.5 }}>{label}</Text>
                  <Toggle on={toggles[k]} onChange={(v) => setToggles((s) => ({ ...s, [k]: v }))} aria-label={label} />
                </div>
              ))}
              <Eyebrow style={{ margin: "16px 0 10px" }}>Priatelia · pills</Eyebrow>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 9 }}>
                {Object.keys(pills).map((n) => (
                  <Pill key={n} on={pills[n]} onClick={() => setPills((s) => ({ ...s, [n]: !s[n] }))}>
                    {pills[n] && <BuzerIcon name="check" size={14} color="currentColor" strokeWidth={2.6} />}{n}
                  </Pill>
                ))}
              </div>
            </Card>
          </div>
        </Section>

        <Section eyebrow="03 — The big unification" title="Jeden zoznam, tri úlohy">
          <Segmented options={[{ value: "person", label: "Ľudia" }, { value: "activity", label: "Rebríček" }, { value: "shop", label: "Obchod" }]}
            value={listView} onChange={setListView} />
          <List style={{ marginTop: 16, maxWidth: 460 }}>
            {listView === "person" && persons.map((r, i) => (
              <ListRow key={i} lead={<Avatar initial={r.initial} tone={r.tone} />} title={r.title} meta={r.meta}
                trailing={<div style={{ display: "flex", gap: 6 }}>
                  <IconButton variant="brand" size="sm" icon="chevron" aria-label="Profil" />
                  <IconButton variant="neutral" size="sm" icon="mail" aria-label="Správa" />
                </div>} />
            ))}
            {listView === "activity" && activity.map((r, i) => (
              <ListRow key={i} rank={r.rank} lead={<Avatar initial={r.initial} tone={r.tone} />} title={r.title} meta={r.meta}
                trailing={<span className="bz-points">{r.points}</span>} />
            ))}
            {listView === "shop" && shop.map((r, i) => (
              <ListRow key={i} lead={<Lead shape="rounded" style={{ background: r.tone, color: "#fff" }}><BuzerIcon name={r.icon} size={19} color="currentColor" /></Lead>}
                title={r.title} meta={r.meta}
                trailing={<Button variant="primary" size="sm">{r.price}</Button>} />
            ))}
          </List>
        </Section>

        <Section eyebrow="04 — Gamification" title="Body, úrovne a odznaky">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 22 }}>
            <StatCard value="15" label="Body dnes" onInfo={() => {}} />
            <StatCard value="1/8" label="Splnené" />
            <StatCard value={<BuzerIcon name="flame" size={28} color="var(--streak)" />} numColor="var(--streak)" label="Dní v sérii" />
          </div>
          <Card style={{ marginBottom: 22 }}>
            <LevelProgress level={4} title="Ešte sa učí chodiť" hint="75 b. do „Prvý boss padol“" pct={62} />
          </Card>
          <Card style={{ marginBottom: 22 }}>
            <Eyebrow style={{ marginBottom: 10 }}>Habit progres · thin bar</Eyebrow>
            <ProgressBar pct={40} size="sm" />
          </Card>
          <BadgeGrid badges={badges} onPick={() => {}} />
        </Section>

        <Section eyebrow="06 — Micro-primitívy" title="Štítky, nadpisy a stavy">
          <Card style={{ marginBottom: 18 }}>
            <Caption style={{ marginBottom: 10 }}>Tag · tóny</Caption>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <Tag tone="brand">VÝZVA</Tag>
              <Tag tone="brand" size="xs">+15</Tag>
              <Tag tone="streak" icon="flame">7</Tag>
              <Tag tone="success">SPLNENÉ</Tag>
              <Tag tone="info">PRESKOČENÉ</Tag>
              <Tag tone="neutral" size="xs">TY</Tag>
              <Tag tone="danger">PO TERMÍNE</Tag>
              <Tag tone="white">BIELY</Tag>
            </div>
          </Card>

          {/* v29 — náročnosť + séria */}
          <Card style={{ marginBottom: 18 }}>
            <Caption style={{ marginBottom: 10 }}>DifficultyTag · StreakTag · LeadWithStars</Caption>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 12 }}>
              <DifficultyTag level="easy" />
              <DifficultyTag level="normal" />
              <DifficultyTag level="hard" size="xs" />
              <DifficultyTag level="hard" showLabel={false} />
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 12 }}>
              <StreakTag streak={7} unit="day" extended />
              <StreakTag streak={7} unit="day" />
              <StreakTag streak={5} unit="times" extended size="xs" />
              <StreakTag streak={3} unit="day" extended lite />
            </div>
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
              <LeadWithStars level="easy"><BuzerIcon name="water" size={18} color="currentColor" /></LeadWithStars>
              <LeadWithStars level="normal" active><BuzerIcon name="book" size={18} color="currentColor" /></LeadWithStars>
              <LeadWithStars level="hard" size="sm"><BuzerIcon name="dumbbell" size={16} color="currentColor" /></LeadWithStars>
            </div>
          </Card>

          <Card style={{ marginBottom: 18 }}>
            <Caption style={{ marginBottom: 10 }}>DifficultyPicker (formulár návyku)</Caption>
            <DifficultyPicker value={diff} onChange={setDiff} />
          </Card>

          <Card style={{ marginBottom: 18 }}>
            <Caption style={{ marginBottom: 4 }}>SectionHeader · varianty</Caption>
            <SectionHeader label="Bez termínu" count={3} />
            <SectionHeader label="Uložené rady" icon="pin" />
            <SectionHeader label="Rebríček" icon="trophy" count={12} />
          </Card>

          <Card style={{ marginBottom: 18 }}>
            <Caption style={{ marginBottom: 12 }}>Caption · popisky + ikona</Caption>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Caption>Kategória</Caption>
              <Caption>Špecifikácia</Caption>
              <Caption icon="folder">Nákupy</Caption>
              <Caption icon="calendar">2h vopred</Caption>
            </div>
          </Card>

          <Card>
            <Caption style={{ marginBottom: 12 }}>StateDot · stavy · CheckButton</Caption>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><StateDot state="done" /> splnené</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><StateDot state="miss" /> vynechané</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><StateDot state="skip" /> preskočené</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><StateDot state="off" /> nič</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><StateDot state="muted" /> neplán.</span>
              <CheckButton done={false} />
              <CheckButton done={true} />
            </div>
          </Card>
        </Section>

        {/* v30 — galéria ozdôb na hlavu. Vizuál sa nedá overiť v CI (WSL nemá chromium),
            takže sa kontroluje tu naživo: prepínač tém + dark hore na stránke prekreslí
            aj ozdoby, keďže ich farby idú výhradne cez tokeny. */}
        <Section eyebrow="07 — Obchod" title="Ozdoby na hlavu">
          <Micro style={{ display: "block", marginBottom: 14 }}>
            {HEAD_FLAIR_CODES.length} ozdôb · kresba sedí na kruh hlavy v každej veľkosti avatara.
            Prepni tému aj dark hore — ozdoby sa musia prefarbiť s ňou.
          </Micro>
          <Card style={{ padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(96px,1fr))", gap: 18 }}>
              {HEAD_FLAIR_CODES.map((code) => (
                <div key={code} style={{ textAlign: "center" }}>
                  <div style={{ display: "flex", justifyContent: "center", height: 78, alignItems: "center" }}>
                    <HeadFlair code={code} size={54} preview />
                  </div>
                  <Micro>{HEAD_FLAIRS[code].label}</Micro>
                </div>
              ))}
            </div>
          </Card>
          <Micro style={{ display: "block", margin: "18px 0 8px" }}>
            Tá istá ozdoba v skutočných veľkostiach avatara (18 · 27 · 36 · 68 · 96 px):
          </Micro>
          <Card style={{ padding: 16 }}>
            <div style={{ display: "flex", gap: 22, alignItems: "flex-end", flexWrap: "wrap" }}>
              {[18, 27, 36, 68, 96].map((sz) => (
                <span key={sz} style={{ position: "relative", display: "inline-flex", width: sz, height: sz,
                  borderRadius: "50%", background: "var(--neutral-surface-3)", border: "1px solid var(--neutral-border)" }}>
                  <HeadFlair code={kitchenFlair} size={sz} />
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 16 }}>
              {HEAD_FLAIR_CODES.map((code) => (
                <Pill key={code} on={kitchenFlair === code} onClick={() => setKitchenFlair(code)}>
                  {HEAD_FLAIRS[code].label}
                </Pill>
              ))}
            </div>
          </Card>
        </Section>

        <Section eyebrow="05 — Overlays" title="Sheet, modal a FAB">
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Button variant="primary" onClick={() => setSheet(true)}>Otvoriť sheet</Button>
            <Micro>FAB je dole vpravo →</Micro>
          </div>
        </Section>
      </main>

      <Fab onClick={() => setSheet(true)} />

      {sheet && (
        <Sheet title="Obchod za body" icon="shop" flush onClose={() => setSheet(false)}
          footer={<>
            <Button variant="ghost" onClick={() => setSheet(false)}>Zrušiť</Button>
            <Button variant="primary" block onClick={() => setSheet(false)}>Uložiť</Button>
          </>}>
          {shop.map((r, i) => (
            <ListRow key={i} lead={<Lead shape="rounded" style={{ background: r.tone, color: "#fff" }}><BuzerIcon name={r.icon} size={19} color="currentColor" /></Lead>}
              title={r.title} meta={r.meta} trailing={<Button variant="primary" size="sm">{r.price}</Button>} />
          ))}
        </Sheet>
      )}
    </div>
  );
}
